import { useTranslation } from 'react-i18next';
import { LOCALES, type Locale } from '@jisy/shared';
import { currentLocale, setLocale } from '../i18n/index.js';
import styles from './LanguageFlags.module.css';

/**
 * Выбор языка на экране входа: четыре кнопки в ряд, как в макете шапки.
 *
 * Здесь пользователь ещё не вошёл, поэтому выбор ложится только в браузер — на сервер его
 * относит `LanguagePicker` после входа.
 *
 * Флаги нарисованы контурами, а не эмодзи: в Windows флаговые эмодзи не отрисовываются вовсе
 * и вместо знака остаётся пара букв.
 */

const FLAGS: Record<Locale, React.ReactElement> = {
  ru: (
    <>
      <rect width="60" height="40" fill="#ffffff" />
      <rect y="13.33" width="60" height="13.34" fill="#0039a6" />
      <rect y="26.67" width="60" height="13.33" fill="#d52b1e" />
    </>
  ),
  en: (
    <>
      <rect width="60" height="40" fill="#012169" />
      <path d="M0 0 60 40M60 0 0 40" stroke="#ffffff" strokeWidth="8" />
      <path d="M0 0 60 40M60 0 0 40" stroke="#c8102e" strokeWidth="4" />
      <path d="M30 0V40M0 20H60" stroke="#ffffff" strokeWidth="13" />
      <path d="M30 0V40M0 20H60" stroke="#c8102e" strokeWidth="8" />
    </>
  ),
  de: (
    <>
      <rect width="60" height="40" fill="#000000" />
      <rect y="13.33" width="60" height="13.34" fill="#dd0000" />
      <rect y="26.67" width="60" height="13.33" fill="#ffce00" />
    </>
  ),
  es: (
    <>
      <rect width="60" height="40" fill="#aa151b" />
      <rect y="10" width="60" height="20" fill="#f1bf00" />
    </>
  ),
};

/**
 * Флаг языка. Вынесен из ряда кнопок, потому что тот же рисунок нужен в выборе языка под шапкой:
 * размер и обводку задаёт вызывающий — на синем экране входа и на белом поповере они разные.
 */
export function LocaleFlag({
  locale,
  className,
}: {
  locale: Locale;
  className?: string;
}): React.ReactElement {
  return (
    <svg className={className} viewBox="0 0 60 40" aria-hidden="true" focusable="false">
      {FLAGS[locale]}
    </svg>
  );
}

export function LanguageFlags(): React.ReactElement {
  const { t } = useTranslation();
  const active = currentLocale();

  return (
    <div className={styles.row} role="group" aria-label={t('settings.language')}>
      {LOCALES.map((locale) => (
        <button
          key={locale}
          type="button"
          className={styles.item}
          aria-pressed={locale === active}
          onClick={() => setLocale(locale)}
        >
          <LocaleFlag locale={locale} className={styles.flag} />
          <span className={styles.code}>{locale.toUpperCase()}</span>
          <span className="visually-hidden">{t(`locales.${locale}`)}</span>
        </button>
      ))}
    </div>
  );
}
